import * as R from 'ramda'

export const AccessType = {
  ANNONYMOUS: 'ANNONYMOUS',
  SUBSCRIBER_VIEW: 'SUBSCRIBER_VIEW',
  SUBSCRIBER_CREATE: 'SUBSCRIBER_CREATE',
  SUBSCRIBER_UPDATE: 'SUBSCRIBER_UPDATE',
  SUBSCRIBER_DELETE: 'SUBSCRIBER_DELETE',
  NOTIFICATION_SEND: 'NOTIFICATION_SEND',
  NOTIFICATION_VIEW_OWNED: 'NOTIFICATION_VIEW_OWNED',
  NOTIFICATION_VIEW_ALL: 'NOTIFICATION_VIEW_ALL',
  NOTIFICATION_VIEW_SENT_TO_EVERYONE: 'NOTIFICATION_VIEW_SENT_TO_EVERYONE',
  NOTIFICATION_VIEW_SENT_BY_OTHER: 'NOTIFICATION_VIEW_SENT_BY_OTHER',
  NOTIFICATION_UNSENT_OWNED: 'NOTIFICATION_UNSENT_OWNED',
  NOTIFICATION_UNSENT_ALL: 'NOTIFICATION_UNSENT_ALL',
  ADMINISTRATION: 'ADMINISTRATION',
  PLACE_PENDING_ACTIVATE: 'PLACE_PENDING_ACTIVATE',
}

// suspend status logic can be merged with access control in the future
export const determineAccess = (
  { permissions, groups, isPlaceSuspended },
  accesses,
  context = {},
) => {
  const restrictionContext = { groups }
  if (isPlaceSuspended) {
    return {
      isGranted: false,
      restrictionContext,
    }
  }

  if (R.isEmpty(accesses))
    return {
      isGranted: true,
      restrictionContext,
    }
  else if (R.isNil(accesses)) return { isGranted: false, restrictionContext }
  const isPermissionGranted = R.reduce(
    (acc, access) => R.or(acc, R.includes(access, permissions)),
    false,
    accesses,
  )

  const isGroupAccessGranted = !R.isEmpty(groups)
    ? R.compose(
        R.ifElse(R.isNil, R.always(true), R.includes(R.__, groups)),
        R.prop('group'),
      )(context)
    : true

  return {
    isGranted: isPermissionGranted && isGroupAccessGranted,
    restrictionContext,
  }
}

export const APIPermissionTypes = {
  SUBSCRIBER_CREATE: 'SUBSCRIBER_CREATE',
  SUBSCRIBER_VIEW: 'SUBSCRIBER_VIEW',
  SUBSCRIBER_EDIT: 'SUBSCRIBER_EDIT',
  SUBSCRIBER_DELETE: 'SUBSCRIBER_DELETE',
  NOTIFICATION_VIEW_MY: 'NOTIFICATION_VIEW_MY',
  NOTIFICATION_SEND: 'NOTIFICATION_SEND',
  NOTIFICATION_VIEW_SENT: 'NOTIFICATION_VIEW_SENT',
  NOTIFICATION_VIEW_SENT_EVERYONE: 'NOTIFICATION_VIEW_SENT_EVERYONE',
  NOTIFICATION_UNSEND_MY: 'NOTIFICATION_UNSEND_MY',
  ALL_PERMISSIONS: 'ALL_PERMISSIONS',
}

const permissionFromAPIPermission = R.cond([
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_CREATE),
    R.always(AccessType.SUBSCRIBER_CREATE),
  ],
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_VIEW),
    R.always(AccessType.SUBSCRIBER_VIEW),
  ],
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_EDIT),
    R.always(AccessType.SUBSCRIBER_UPDATE),
  ],
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_DELETE),
    R.always(AccessType.SUBSCRIBER_DELETE),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_VIEW_MY),
    R.always(AccessType.NOTIFICATION_VIEW_OWNED),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_SEND),
    R.always(AccessType.NOTIFICATION_SEND),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_VIEW_SENT),
    R.always(AccessType.NOTIFICATION_VIEW_SENT_BY_OTHER),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_VIEW_SENT_EVERYONE),
    R.always(AccessType.NOTIFICATION_VIEW_SENT_TO_EVERYONE),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_UNSEND_MY),
    R.always(AccessType.NOTIFICATION_UNSENT_OWNED),
  ],
  [
    R.equals(APIPermissionTypes.ALL_PERMISSIONS),
    R.always(AccessType.ADMINISTRATION),
  ],
  [R.T, R.identity],
])

export const localizeIdFromAPIPermission = R.cond([
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_CREATE),
    R.always('accessControl.permissions.subscriber.create'),
  ],
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_VIEW),
    R.always('accessControl.permissions.subscriber.view'),
  ],
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_EDIT),
    R.always('accessControl.permissions.subscriber.edit'),
  ],
  [
    R.equals(APIPermissionTypes.SUBSCRIBER_DELETE),
    R.always('accessControl.permissions.subscriber.delete'),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_VIEW_MY),
    R.always('accessControl.permissions.notification.view'),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_SEND),
    R.always('accessControl.permissions.notification.send'),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_VIEW_SENT),
    R.always('accessControl.permissions.notification.sendByOther'),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_VIEW_SENT_EVERYONE),
    R.always('accessControl.permissions.notification.sendToEveryone'),
  ],
  [
    R.equals(APIPermissionTypes.NOTIFICATION_UNSEND_MY),
    R.always('accessControl.permissions.notification.unsend'),
  ],
  [
    R.equals(APIPermissionTypes.ALL_PERMISSIONS),
    R.always('accessControl.permissions.admin'),
  ],
  [R.T, R.identity],
])

const addDefaultPermissions = R.concat([AccessType.NOTIFICATION_VIEW_OWNED])

const getPlaceByUserData = R.curry((selectedPlaceIndex, userData) =>
  R.path(['places', selectedPlaceIndex])(userData),
)

const getPermissionInfoByRoles = (roles) => ({
  permissions: R.compose(
    R.uniq,
    addDefaultPermissions,
    R.map(permissionFromAPIPermission),
    flatMapNonNil(R.prop('key')),
    flatMapNonNil(R.prop('permissions')),
  )(roles),
  groups: R.compose(
    R.uniq,
    flatMapNonNil(R.prop('id')),
    flatMapNonNil(R.prop('groups')),
  )(roles),
})

export const getPermissionInfoByPlace = (place) => {
  const roles = R.propOr([], 'roles', place)
  const isPlaceSuspended = R.path(['info', 'isSuspended'], place)
  return {
    ...getPermissionInfoByRoles(roles),
    isPlaceSuspended,
  }
}

export const getPermissionInfoByUserData = R.compose(
  getPermissionInfoByPlace,
  getPlaceByUserData,
)

// userPlaceByIndex :: Index -> User -> Maybe Place
export const userPlaceByIndex = R.curry((index, user) =>
  R.path(['places', index], user),
)

export const userPlaceBySelectedPlace = R.curry((selectedPlace, user) =>
  R.compose(
    R.find(R.propEq('id', selectedPlace.id)),
    R.propOr([], 'places'),
  )(user),
)

export const defaultSelectedPlaceIndex = 0

export const defaultUserPlace = userPlaceByIndex(defaultSelectedPlaceIndex)

// -- Utils

const flatMapNonNil = (transform) =>
  R.compose(
    R.filter(R.o(R.not, R.isNil)),
    R.flatten,
    R.map(transform),
    R.defaultTo([]),
  )

export const apiPermissions = [
  {
    id: 1,
    name: 'Create Subscribers',
    key: 'SUBSCRIBER_CREATE',
  },
  {
    id: 2,
    name: 'View Subscribers',
    key: 'SUBSCRIBER_VIEW',
  },
  {
    id: 3,
    name: 'Edit Subscribers',
    key: 'SUBSCRIBER_EDIT',
  },
  // {
  //   id: 4,
  //   name: 'Delete Subscribers',
  //   key: 'SUBSCRIBER_DELETE',
  // },
  {
    id: 9,
    name: 'View My Notifications',
    key: 'NOTIFICATION_VIEW_MY',
  },
  {
    id: 10,
    name: 'View Sent to Everyone',
    key: 'NOTIFICATION_VIEW_SENT_EVERYONE',
  },
  {
    id: 5,
    name: 'Send Notifications',
    key: 'NOTIFICATION_SEND',
  },
  {
    id: 6,
    name: 'View Sent by Others',
    key: 'NOTIFICATION_VIEW_SENT',
  },
  {
    id: 7,
    name: 'Unsend My Notifications',
    key: 'NOTIFICATION_UNSEND_MY',
  },
]

export const viewMyNotification = R.find(R.propEq('id', 9), apiPermissions)
